import { APP } from './tokens';
import type { VersionNode } from '../../types';
import type { VersionTimelineEntry } from '../../lib/versionTimeline';

// ─── Color mapping ──────────────────────────────

type VersionStyle = {
  headerBg: string;
  borderColor: string;
  legendColor: string;
};

function getVersionStyle(node: VersionNode, _index: number, _total: number): VersionStyle {
  if (node.isCurrent) {
    return {
      headerBg: APP.versionCurrent,
      borderColor: APP.versionCurrent,
      legendColor: APP.versionCurrent,
    };
  }
  const op = (node.operation ?? '').toLowerCase();
  if (op === 'signed') {
    return {
      headerBg: APP.versionSigned,
      borderColor: APP.versionSigned,
      legendColor: APP.versionSigned,
    };
  }
  return {
    headerBg: APP.versionOriginal,
    borderColor: APP.versionOriginal,
    legendColor: APP.versionLegendOriginal,
  };
}

function formatVersionDate(date: Date): string {
  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  if (isToday) return 'Today';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getVersionLabel(node: VersionNode, index: number): string {
  const num = `v${index + 1}`;
  if (node.isCurrent) return `${num} — Current`;
  if (node.operation) return `${num} — ${node.operation}`;
  if (index === 0) return `${num} — Original`;
  return num;
}

// ─── Sub-components ─────────────────────────────

function VersionCardHeader({
  label,
  date,
  bgColor,
  isCurrent,
}: {
  label: string;
  date: string;
  bgColor: string;
  isCurrent: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderTopLeftRadius: 6,
        borderTopRightRadius: 6,
        paddingBlock: 6,
        paddingInline: 10,
        backgroundColor: bgColor,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {isCurrent && (
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: APP.versionCurrentDot,
              flexShrink: 0,
            }}
          />
        )}
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: '#FFFFFF',
            fontFamily: APP.fontFamily,
            lineHeight: '12px',
            flexShrink: 0,
          }}
        >
          {label}
        </span>
      </div>
      <span
        style={{
          fontSize: 9,
          fontWeight: 400,
          opacity: isCurrent ? 0.7 : 0.8,
          color: '#FFFFFF',
          fontFamily: APP.fontFamily,
          lineHeight: '12px',
          flexShrink: 0,
        }}
      >
        {date}
      </span>
    </div>
  );
}

function VersionCardBody({ borderColor }: { borderColor: string }) {
  // Placeholder document preview lines
  return (
    <div
      style={{
        width: 220,
        height: 280,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        borderBottomLeftRadius: 6,
        borderBottomRightRadius: 6,
        paddingBlock: 16,
        paddingInline: 14,
        overflow: 'clip',
        gap: 8,
        backgroundColor: APP.bgVersionCard,
        borderLeft: `2px solid ${borderColor}`,
        borderBottom: `2px solid ${borderColor}`,
        borderRight: `2px solid ${borderColor}`,
        boxSizing: 'border-box',
      }}
    >
      {/* Fake document skeleton lines */}
      <div style={{ width: 150, height: 10, borderRadius: 3, backgroundColor: '#E4DDD4' }} />
      <div style={{ width: 113, height: 8, borderRadius: 3, backgroundColor: '#EDEAE6' }} />
      <div style={{ width: 188, height: 1, backgroundColor: '#EDEAE6' }} />
      <div style={{ width: 188, height: 8, borderRadius: 3, backgroundColor: '#EDEAE6' }} />
      <div style={{ width: 160, height: 8, borderRadius: 3, backgroundColor: '#EDEAE6' }} />
      <div style={{ width: 188, height: 8, borderRadius: 3, backgroundColor: '#EDEAE6' }} />
      <div style={{ width: 132, height: 8, borderRadius: 3, backgroundColor: '#EDEAE6' }} />
    </div>
  );
}

function Arrow() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        marginInline: 4,
      }}
    >
      <div style={{ width: 40, height: 2, backgroundColor: APP.versionArrow, flexShrink: 0 }} />
      {/* Triangle arrowhead via CSS borders */}
      <div
        style={{
          width: 0,
          height: 0,
          borderTop: '5px solid transparent',
          borderLeft: `7px solid ${APP.versionArrow}`,
          borderBottom: '5px solid transparent',
          flexShrink: 0,
        }}
      />
    </div>
  );
}

interface LegendItem {
  label: string;
  color: string;
  isBar?: boolean;
}

const LEGEND_ITEMS: LegendItem[] = [
  { label: 'Original', color: APP.versionLegendOriginal },
  { label: 'Signed', color: APP.versionSigned },
  { label: 'Current', color: APP.versionCurrent },
  { label: 'Changed', color: APP.versionChanged, isBar: true },
];

function Legend() {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 24,
        left: 80,
        display: 'flex',
        alignItems: 'center',
        borderRadius: 8,
        paddingBlock: 8,
        paddingInline: 14,
        gap: 16,
        backgroundColor: '#FFFFFF',
        border: `1px solid ${APP.borderQuickAction}`,
      }}
    >
      {LEGEND_ITEMS.map((item) => (
        <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div
            style={{
              width: 10,
              height: item.isBar ? 4 : 10,
              borderRadius: 2,
              backgroundColor: item.color,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 11,
              fontWeight: 400,
              color: APP.textSecondary,
              fontFamily: APP.fontFamily,
              lineHeight: '14px',
              flexShrink: 0,
            }}
          >
            {item.label}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Main component ─────────────────────────────

interface VersionTimelineProps {
  fileName?: string;
  nodes?: VersionNode[];
  entries?: VersionTimelineEntry[];
  onRestore?: (id: string) => void;
  onSelectEntry?: (entry: VersionTimelineEntry) => void;
  onClose?: () => void;
}

function formatEntryDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function originLabel(origin: 'html_edit' | 'clause_flags' | 'plan_doc_edits'): string {
  if (origin === 'clause_flags') return 'Clause Flags';
  if (origin === 'plan_doc_edits') return 'Document edit';
  return 'Edit';
}

function VersionHistoryList({
  entries,
  onSelectEntry,
}: {
  entries: VersionTimelineEntry[];
  onSelectEntry?: (entry: VersionTimelineEntry) => void;
}) {
  if (entries.length === 0) {
    return (
      <div style={{ padding: 24, fontSize: 13, color: APP.textMuted, fontFamily: APP.fontFamily }}>
        No version history yet.
      </div>
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: 24,
        maxWidth: 720,
        width: '100%',
      }}
    >
      {[...entries].reverse().map((entry) => {
        const title = entry.kind === 'draft'
          ? `v${entry.version} · Drafted`
          : `v${entry.version} · ${originLabel(entry.origin)}`;
        const subtext = entry.kind === 'draft'
          ? `${entry.title} · ${entry.source === 'draft-stream' ? 'Generated draft' : 'Imported'}`
          : `${entry.label} · ${entry.acceptedCount} accepted, ${entry.declinedCount} declined`;
        return (
          <button
            key={`${entry.kind}:${entry.documentId}:${entry.kind === 'edit_batch' ? entry.batchId : entry.version}`}
            type="button"
            onClick={() => onSelectEntry?.(entry)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 5,
              borderRadius: 8,
              border: `1px solid ${APP.borderQuickAction}`,
              backgroundColor: '#FFFFFF',
              paddingBlock: 12,
              paddingInline: 14,
              cursor: onSelectEntry ? 'pointer' : 'default',
              textAlign: 'left',
              fontFamily: APP.fontFamily,
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: APP.textPrimary, lineHeight: '16px' }}>
              {title}
            </span>
            <span style={{ fontSize: 12, fontWeight: 400, color: APP.textSecondary, lineHeight: '16px' }}>
              {subtext}
            </span>
            <span style={{ fontSize: 10, fontWeight: 500, color: APP.textMuted, lineHeight: '14px' }}>
              {formatEntryDate(entry.createdAt)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function VersionTimeline({
  fileName,
  nodes = [],
  entries = [],
  onRestore,
  onSelectEntry,
  onClose,
}: VersionTimelineProps) {
  return (
    <div
      style={{
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '0%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── Top Bar ─────────────────────────────── */}
      <div
        style={{
          height: 40,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          paddingInline: 16,
          gap: 8,
          backgroundColor: APP.bgContent,
          borderBottom: `1px solid ${APP.borderQuickAction}`,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: APP.textPrimary,
            fontFamily: APP.fontFamily,
            lineHeight: '16px',
            flexShrink: 0,
          }}
        >
          {fileName ?? 'Untitled'}
        </span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 400,
            color: APP.textSecondary,
            fontFamily: APP.fontFamily,
            lineHeight: '14px',
            flexShrink: 0,
          }}
        >
          — version history
        </span>
        {/* Spacer */}
        <div style={{ flexGrow: 1, flexShrink: 1, flexBasis: '0%' }} />
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            style={{
              borderRadius: 6,
              paddingBlock: 4,
              paddingInline: 10,
              backgroundColor: '#FFFFFF',
              border: `1px solid ${APP.borderQuickAction}`,
              cursor: 'pointer',
              fontFamily: APP.fontFamily,
              fontSize: 11,
              fontWeight: 400,
              color: APP.textPrimary,
              lineHeight: '14px',
            }}
          >
            Close
          </button>
        )}
      </div>

      {/* ── Canvas ──────────────────────────────── */}
      <div
        style={{
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: '0%',
          position: 'relative',
          overflow: 'auto',
          backgroundColor: APP.bgVersionCanvas,
        }}
      >
        {entries.length > 0 ? (
          <VersionHistoryList entries={entries} onSelectEntry={onSelectEntry} />
        ) : (
          <>
        {/* Horizontal card row */}
        <div
          style={{
            position: 'absolute',
            left: 80,
            top: 80,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {nodes.map((node, i) => {
            const vs = getVersionStyle(node, i, nodes.length);
            const label = getVersionLabel(node, i);
            const date = formatVersionDate(node.timestamp);
            return (
              <div key={node.id} style={{ display: 'flex', alignItems: 'center' }}>
                {i > 0 && <Arrow />}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: 220,
                    cursor: 'pointer',
                    marginTop: node.isCurrent ? -20 : 0,
                  }}
                  onClick={() => onRestore?.(node.id)}
                >
                  <VersionCardHeader
                    label={label}
                    date={date}
                    bgColor={vs.headerBg}
                    isCurrent={node.isCurrent}
                  />
                  <VersionCardBody borderColor={vs.borderColor} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <Legend />
          </>
        )}
      </div>
    </div>
  );
}
